import { useEffect, useRef, useState } from 'react';

// Existing copy preserved verbatim — do not edit the body text.
const chapters = [
  {
    location: 'Ezibeleni',
    subtitle: 'Eastern Cape, South Africa',
    theme: 'Imagination & Roots',
    body: "Before instruments and computer programs, there were raw objects. Old paint buckets as drums. iiToti (used cans) as percussion. Vocal distortion effects and make-do horns from bottles - all coming together in crude ensembles resembling a future he hadn't yet discovered. Jazz sounds bleeding from carwashes and taverns into the street. These were not substitutes for music — they were music, in its most honest form.",
  },
  {
    location: 'Durban',
    subtitle: 'KwaZulu-Natal, South Africa',
    theme: 'Proximity & Craft',
    body: 'Varsity days brought him into the same rooms as DJs and producers for the first time — not on stage, but in shared spaces. Watching a roommate move through music production with formal training while he explored the same tools through instinct and curiosity. Two approaches to the same language. Both left their mark.',
  },
  {
    location: 'Johannesburg',
    subtitle: 'Gauteng, South Africa',
    theme: 'Immersion & Belonging',
    body: "South Africa's creative capital did not just show him the industry — it let him inside it. The biggest artists were no longer distant figures. They were neighbours, collaborators, people eating at the same tables. Studio sessions. Joint projects. The gradual, earned realisation that he had always belonged in this world.",
  },
  {
    location: 'Toronto',
    subtitle: 'Canada',
    theme: 'Discipline & Identity',
    body: "This move across continents forced a reckoning: who are you when you are no longer surrounded by the culture that shaped you? Toronto surprised him. A growing diaspora of Afro-electronic enthusiasts — African youth chasing the same sound from different starting points — created space for him to step forward and be heard. That visibility accelerated everything. The creative and the technical converged. The corporate discipline refined the artist. The artist made the discipline meaningful.",
  },
  {
    location: 'Amsterdam',
    subtitle: 'The Netherlands',
    theme: 'Vision & Arrival',
    body: 'Europe. A new stage. The accumulated weight of every city, every session, every doubt and decision — now assembled into a coherent artistic vision. Lulo Lonke arrives not as someone new, but as someone finally, fully present. The roots are intact. The sound is refined. The chapter is open.',
  },
];

const tickPercent = (i) => (i / (chapters.length - 1)) * 100;

function CityCard({ chapter, index, scrollDir, isExiting }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Double-rAF so the browser commits the pre-enter frame before transitioning in.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setEntered(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  // forward scroll ("down") → name enters from left (-50px), exits to left (-24px)
  // backward scroll ("up")   → name enters from right (+50px), exits to right (+24px)
  const enterX = scrollDir === 'up' ? 50 : -50;
  const exitX  = scrollDir === 'up' ? 24 : -24;

  const stateClass = isExiting ? 'exiting' : entered ? 'entered' : 'pre-enter';

  return (
    <div
      className={`journey-card ${stateClass}`}
      style={{ '--enter-x': `${enterX}px`, '--exit-x': `${exitX}px` }}
      data-testid={`journey-card-${chapter.location.toLowerCase()}`}
    >
      <div className="journey-eyebrow">
        {String(index + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
      </div>
      <div className="journey-city-name">{chapter.location}</div>
      <div className="journey-sub-location">{chapter.subtitle}</div>
      <div className="journey-divider" aria-hidden="true" />
      <div className="journey-theme">{chapter.theme.toUpperCase()}</div>
      <p className="journey-body">{chapter.body}</p>
    </div>
  );
}

export default function Journey() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const [scrollDir, setScrollDir] = useState('down');
  // lazy-init these so the first paint already has the correct layout
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  // Mobile-only: which cards have entered the viewport (for one-shot reveal)
  // and which card is currently the most visible (for the progress dots).
  const [mobileEntered, setMobileEntered] = useState(() => new Set());
  const [mobileActive, setMobileActive] = useState(0);

  const sectionRef = useRef(null);
  const anchorsRef = useRef([]);
  const mobileCardRefs = useRef([]);
  const lastScrollYRef = useRef(0);

  // viewport + reduced-motion detection
  useEffect(() => {
    const mm = window.matchMedia('(max-width: 768px)');
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      setIsMobile(mm.matches);
      setReducedMotion(rm.matches);
    };
    apply();
    mm.addEventListener('change', apply);
    rm.addEventListener('change', apply);
    return () => {
      mm.removeEventListener('change', apply);
      rm.removeEventListener('change', apply);
    };
  }, []);

  // scroll direction tracking (only needed when scroll-driven activation is live)
  useEffect(() => {
    if (isMobile || reducedMotion) return undefined;
    lastScrollYRef.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrollDir(y > lastScrollYRef.current ? 'down' : 'up');
      lastScrollYRef.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobile, reducedMotion]);

  // IntersectionObserver — activates city when its anchor enters the viewport
  useEffect(() => {
    if (isMobile || reducedMotion) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.cityIndex);
            if (!Number.isNaN(idx)) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.5, rootMargin: '0px 0px -20% 0px' },
    );
    anchorsRef.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [isMobile, reducedMotion]);

  // Mobile IntersectionObservers:
  //   - enterObserver: fires once per card at threshold 0.3 to add `entered`
  //   - activeObserver: continuously tracks the most visible card for the
  //     progress dots at the top of the section.
  useEffect(() => {
    if (!isMobile || reducedMotion) return undefined;

    const enterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = Number(entry.target.dataset.cardIndex);
          if (Number.isNaN(idx)) return;
          setMobileEntered((prev) => {
            if (prev.has(idx)) return prev;
            const next = new Set(prev);
            next.add(idx);
            return next;
          });
          enterObserver.unobserve(entry.target);
        });
      },
      { threshold: 0.3 },
    );

    const activeObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((a, b) => (a.intersectionRatio > b.intersectionRatio ? a : b));
        const idx = Number(top.target.dataset.cardIndex);
        if (!Number.isNaN(idx)) setMobileActive(idx);
      },
      { threshold: [0.25, 0.5, 0.75] },
    );

    mobileCardRefs.current.forEach((el) => {
      if (!el) return;
      enterObserver.observe(el);
      activeObserver.observe(el);
    });

    return () => {
      enterObserver.disconnect();
      activeObserver.disconnect();
    };
  }, [isMobile, reducedMotion]);

  // exit → 200ms gap → mount new
  useEffect(() => {
    if (activeIndex === displayIndex) return undefined;
    setIsExiting(true);
    const t = setTimeout(() => {
      setDisplayIndex(activeIndex);
      setIsExiting(false);
    }, 200);
    return () => clearTimeout(t);
  }, [activeIndex, displayIndex]);

  const goTo = (idx) => {
    if (isMobile || reducedMotion) {
      // mobile / reduced motion: just update the active city; no scroll
      setActiveIndex(idx);
      return;
    }
    const anchor = anchorsRef.current[idx];
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const fillPercent = tickPercent(activeIndex);

  // ---------- Reduced motion: render all cities statically -----------------
  if (reducedMotion) {
    return (
      <section id="journey" className="journey-section journey-section--static" data-testid="journey-section">
        <div className="section-inner">
          <div className="journey-intro">
            <div className="section-label">03 &mdash; The Journey</div>
            <h2 className="journey-heading display">
              Five cities.<br /><em>One continuous becoming.</em>
            </h2>
          </div>
          {chapters.map((ch, i) => (
            <div key={ch.location} className="journey-card journey-card--static">
              <div className="journey-eyebrow">
                {String(i + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
              </div>
              <div className="journey-city-name">{ch.location}</div>
              <div className="journey-sub-location">{ch.subtitle}</div>
              <div className="journey-divider" aria-hidden="true" />
              <div className="journey-theme">{ch.theme.toUpperCase()}</div>
              <p className="journey-body">{ch.body}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  // ---------- Mobile: stacked vertical storybook ---------------------------
  // Each card gets its own scroll-triggered reveal (IntersectionObserver at
  // threshold 0.3) and reuses the desktop staggered animation CSS. A sticky
  // row of progress dots at the top of the section tracks the active card.
  if (isMobile) {
    return (
      <section
        id="journey"
        className="journey-section journey-section--mobile"
        data-testid="journey-section"
      >
        <div className="journey-progress-mobile" role="tablist" aria-label="Journey chapters">
          {chapters.map((ch, i) => {
            const state = i === mobileActive ? 'active' : i < mobileActive ? 'past' : 'future';
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === mobileActive}
                aria-label={`Jump to ${ch.location}`}
                className={`journey-progress-dot journey-progress-dot--${state}`}
                onClick={() => {
                  const el = mobileCardRefs.current[i];
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid={`journey-progress-dot-mobile-${i}`}
              />
            );
          })}
        </div>
        <div className="section-inner">
          <div className="journey-intro">
            <div className="section-label">03 &mdash; The Journey</div>
            <h2 className="journey-heading display">
              Five cities.<br /><em>One continuous becoming.</em>
            </h2>
          </div>
          {chapters.map((ch, i) => {
            const entered = mobileEntered.has(i);
            const stateClass = entered ? 'entered' : 'pre-enter';
            return (
              <article
                key={ch.location}
                ref={(el) => (mobileCardRefs.current[i] = el)}
                data-card-index={i}
                className={`journey-card journey-card--mobile ${stateClass}`}
                style={{ '--enter-x': '-50px' }}
                data-testid={`journey-card-${ch.location.toLowerCase()}`}
              >
                <div className="journey-eyebrow">
                  {String(i + 1).padStart(2, '0')} / {String(chapters.length).padStart(2, '0')}
                </div>
                <div className="journey-city-name">{ch.location}</div>
                <div className="journey-sub-location">{ch.subtitle}</div>
                <div className="journey-divider" aria-hidden="true" />
                <div className="journey-theme">{ch.theme.toUpperCase()}</div>
                <p className="journey-body">{ch.body}</p>
              </article>
            );
          })}
        </div>
      </section>
    );
  }

  // ---------- Desktop: scroll-driven sticky layout -------------------------
  return (
    <section
      id="journey"
      ref={sectionRef}
      className="journey-section"
      data-testid="journey-section"
    >
      {/* Scroll anchors — distributed at 20% intervals of the section height. */}
      {chapters.map((_, i) => (
        <div
          key={i}
          ref={(el) => (anchorsRef.current[i] = el)}
          data-city-index={i}
          className="journey-anchor"
          style={{ top: `${(i / chapters.length) * 100 + 10}%` }}
          aria-hidden="true"
        />
      ))}

      <div className="journey-sticky-content">
        <div className="journey-layout">
          {/* Left timeline rail */}
          <div className="journey-rail" aria-hidden="true">
            <div className="journey-rail-track">
              <div
                className="journey-rail-fill"
                style={{ height: `${fillPercent}%` }}
              />
              <div
                className="journey-rail-dot"
                style={{ top: `calc(${fillPercent}% - 3.5px)` }}
              />
            </div>
            {chapters.map((ch, i) => (
              <div
                key={i}
                className="journey-tick"
                style={{ top: `${tickPercent(i)}%` }}
              >
                <div className="journey-tick-line" />
                <span
                  className={`journey-tick-label${i <= activeIndex ? ' active' : ''}`}
                >
                  {ch.location.toUpperCase()}
                </span>
              </div>
            ))}
          </div>

          {/* Right content area */}
          <div className="journey-content">
            <div className="section-label">03 &mdash; The Journey</div>
            <CityCard
              key={displayIndex}
              chapter={chapters[displayIndex]}
              index={displayIndex}
              scrollDir={scrollDir}
              isExiting={isExiting}
            />
          </div>

          {/* Progress dots */}
          <div className="journey-progress" role="tablist" aria-label="Journey chapters">
            {chapters.map((ch, i) => {
              const state = i === activeIndex ? 'active' : i < activeIndex ? 'past' : 'future';
              return (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeIndex}
                  aria-label={`Jump to ${ch.location}`}
                  className={`journey-progress-dot journey-progress-dot--${state}`}
                  onClick={() => goTo(i)}
                  data-testid={`journey-progress-dot-${i}`}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
